import { useState, MouseEvent } from 'react';
import { Avatar, Box } from '@mui/material';
import sanitizeHtml from 'sanitize-html';
import Linkify from 'linkify-react';
import classNames from 'classnames';
import { noop } from 'lodash';
import { ReactionType, Reply } from '../../../../generated/graphql';
import { PostReactions } from '@client/new/community/components/PostReactions';
import { fromNow } from '../utils/dateTimeUtils';
import { ColorSets } from '@client/new/community/utils/ReactionChipColorVariants.map';
import { ActionMenu } from '@client/new/community/components/ActionMenu';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBadgeCheck } from '@fortawesome/pro-solid-svg-icons';
import auth from '@client/components/auth';
import { SensitiveContent } from './SensitiveContent';

export type CommunityReplyCellProps = {
  reply: Reply;
  communityId: number;
  isUserCommunityOwner: boolean;
  postId: string;
  onDelete?: () => void;
  onBlock?: (communityId: number, userId: number) => void;
  onToggleReaction?: (reactionType: ReactionType) => void;
};

/**
 * @param {string} text
 * @param {Author} author
 * @param {Array<Reaction>} reactions
 * @param {number} communityId
 * @param {string} postId
 * @param {any} createdAt
 * @param {() => void} onDelete
 * @param {(communityId: number, userId: number) => void} onBlock
 * @return {JSX.Element}
 * @constructor
 */
export const CommunityReplyCell = ({
  communityId,
  reply: { text, author, reactions, createdAt, id, isSensitiveContent },
  isUserCommunityOwner,
  onDelete = noop,
  onBlock = noop,
  onToggleReaction = noop,
}: CommunityReplyCellProps): JSX.Element => {
  const isAuthor: boolean = author.badges.includes('own');
  const isCreatorsPost: boolean = author.badges.includes('creator');
  const isAdmin = auth.isAdministrator();
  const userId = author.id;
  const showBlockUser =
    ((isUserCommunityOwner && !isAuthor) || isAdmin) && !isCreatorsPost;

  const [hideSensitiveContent, setHideSensitiveContent] =
    useState<boolean>(isSensitiveContent);

  const handleHideSensitiveContent = (ev: MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    ev.stopPropagation();
    setHideSensitiveContent(false);
  };

  return (
    <Box
      className={classNames('flex', 'mb-3', 'max-w-3xl', {
        'text-white': isAuthor,
      })}
      id={id}
    >
      <Avatar
        className="self-end mr-2"
        alt={author.username}
        src={author.profilePicture}
        data-testid="avatar"
      />
      <Box
        className={classNames('rounded-[24px]', 'py-3', 'px-4', 'w-full', {
          'bg-blue-1': isAuthor,
          'bg-primary-150': !isAuthor,
        })}
      >
        <Box className="flex justify-between">
          <Box>
            <span className="font-bold">{author.username} </span>
            {author.badges.includes('creator') && !isAuthor && (
              <FontAwesomeIcon icon={faBadgeCheck} className="text-purple-1" />
            )}
            {!isAuthor && <span className={'text-gray-2'}> #{author.id}</span>}
          </Box>
          {(isUserCommunityOwner || isAuthor || isAdmin) && (
            <ActionMenu
              onDelete={onDelete}
              onBlock={() => onBlock(communityId, userId)}
              item="comment"
              showBlockUser={showBlockUser}
            />
          )}
        </Box>
        {hideSensitiveContent ? (
          <SensitiveContent
            handleHideSensitiveContent={handleHideSensitiveContent}
          />
        ) : (
          <>
            <Linkify
              options={{
                truncate: 42,
                target: '_blank',
                attributes: {
                  onClick: (ev: React.MouseEvent<HTMLDivElement>) => {
                    ev.stopPropagation();
                  },
                },
                className: classNames({
                  'text-white': isAuthor,
                }),
              }}
            >
              {sanitizeHtml(text, {
                allowedTags: ['a'],
                disallowedTagsMode: 'escape',
              })}
            </Linkify>
            <Box className="flex justify-between items-baseline">
              <PostReactions
                reactions={reactions}
                colorSet={isAuthor ? ColorSets.Owner : ColorSets.Thread}
                onToggleReaction={onToggleReaction}
              />
              <span className="text-xs self-end">{fromNow(createdAt)}</span>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
